import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';

const QuickReserveModal = ({ isOpen, onClose, board, onReservationComplete }) => {
  // Helper function to get today's date in YYYY-MM-DD format in local timezone
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [timeSlots, setTimeSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [reservationNotes, setReservationNotes] = useState('');
  const [isReserving, setIsReserving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [dateAvailability, setDateAvailability] = useState({});
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);

  // Generate next 7 days for date selection
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 7; i++) {
      // Create date in local timezone, avoiding UTC conversion issues
      const localDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);

      // Get YYYY-MM-DD in local timezone (not UTC)
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const dateValue = `${year}-${month}-${day}`;

      const slotCount = dateAvailability[dateValue] || 0;
      dates.push({
        value: dateValue,
        dayLabel: localDate.toLocaleDateString('en-US', { weekday: 'short' }),
        dateLabel: localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        isToday: i === 0,
        hasSlots: slotCount > 0,
        slotCount: slotCount
      });
    }
    return dates;
  };

  const loadTimeSlots = useCallback(async () => {
    if (!board || !selectedDate) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/slots/${board.id}?date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load time slots');
      }

      const slotsData = await response.json();

      // Get current time
      const now = new Date();
      const isToday = selectedDate === getTodayDateString();

      // Filter to only available slots (no reservation_id and not blocked)
      // For today, also filter out slots that have already passed
      const availableSlots = slotsData.filter(slot => {
        if (slot.reservation_id || slot.is_blocked) return false;

        // If it's today, check if the slot start time has passed
        if (isToday) {
          const slotStartTime = new Date(slot.start_time);
          if (slotStartTime <= now) {
            return false; // Slot has already started or passed
          }
        }

        return true;
      });

      setTimeSlots(availableSlots);
    } catch (error) {
      console.error('Error loading time slots:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [board, selectedDate]);

  const checkDateAvailability = useCallback(async () => {
    if (!board) return;

    setIsCheckingAvailability(true);
    const availability = {};

    try {
      const now = new Date();
      const dates = [];
      const today = new Date();
      for (let i = 0; i < 7; i++) {
        // Create date in local timezone
        const localDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
      }

      // Check each date in parallel
      const promises = dates.map(async (date) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/slots/${board.id}?date=${date}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          if (response.ok) {
            const slotsData = await response.json();
            const isToday = date === getTodayDateString();

            // Filter slots the same way we do in loadTimeSlots
            const availableSlots = slotsData.filter(slot => {
              if (slot.reservation_id || slot.is_blocked) return false;

              // For today, filter out past times
              if (isToday) {
                const slotStartTime = new Date(slot.start_time);
                if (slotStartTime <= now) {
                  return false;
                }
              }

              return true;
            });

            availability[date] = availableSlots.length;
          } else {
            availability[date] = 0;
          }
        } catch (err) {
          console.error(`Error checking availability for ${date}:`, err);
          availability[date] = 0;
        }
      });

      await Promise.all(promises);
      setDateAvailability(availability);
    } catch (error) {
      console.error('Error checking date availability:', error);
    } finally {
      setIsCheckingAvailability(false);
      // Load slots for the initially selected date
      loadTimeSlots();
    }
  }, [board, loadTimeSlots]);

  // Check slot availability for all dates when modal opens (but not on every re-render)
  useEffect(() => {
    if (isOpen && board && !hasLoadedInitialData) {
      setHasLoadedInitialData(true);
      checkDateAvailability();
    } else if (!isOpen) {
      // Reset when modal closes
      setHasLoadedInitialData(false);
    }
    // Only run when modal opens/closes or board changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, board?.id]);

  // Load time slots when date changes
  useEffect(() => {
    // Only load time slots when date changes, not when showing confirmation
    if (isOpen && board && selectedDate && !showConfirmation) {
      loadTimeSlots();
    }
  }, [selectedDate, isOpen, board, showConfirmation, loadTimeSlots]);

  const handleSlotSelect = (slot) => {
    // Single slot selection - clicking a slot selects it (or deselects if already selected)
    setSelectedSlots(prevSelected => {
      // If clicking the already selected slot, deselect it
      if (prevSelected.length === 1 && prevSelected[0].id === slot.id) {
        return [];
      }

      // Otherwise, replace selection with this slot
      return [slot];
    });
  };

  const handleContinue = () => {
    // Show confirmation (no validation needed for single slot)
    setShowConfirmation(true);
  };

  const handleReservation = async () => {
    if (selectedSlots.length === 0) return;

    setIsReserving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          timeSlotIds: selectedSlots.map(s => s.id),
          notes: reservationNotes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create reservation');
      }

      // Success!
      const reservation = await response.json();
      console.log('✅ Reservation created successfully:', reservation);

      // Close the main reservation modal first
      console.log('Closing main modal via onClose()');
      onClose();

      // Notify parent with reservation data - parent will show success modal
      if (onReservationComplete) {
        console.log('Calling onReservationComplete callback with reservation:', reservation);
        onReservationComplete(reservation);
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      setError(error.message);
    } finally {
      setIsReserving(false);
    }
  };

  const formatTime = (dateString, gymTimezone) => {
    if (!gymTimezone) {
      // Fallback to previous behavior if no timezone provided
      return new Date(dateString.replace('Z', '')).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // Parse the datetime and format it in the gym's timezone
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: gymTimezone
    });
  };

  const getTimeOfDay = (dateString, gymTimezone) => {
    let hour;

    if (!gymTimezone) {
      // Fallback to previous behavior if no timezone provided
      const date = new Date(dateString.replace('Z', ''));
      hour = date.getHours();
    } else {
      // Get the hour in the gym's timezone
      const date = new Date(dateString);
      const timeInGymTz = date.toLocaleString('en-US', {
        timeZone: gymTimezone,
        hour12: false,
        hour: '2-digit'
      });
      hour = parseInt(timeInGymTz);
    }

    if (hour >= 6 && hour < 12) return 'Morning';
    if (hour >= 12 && hour < 17) return 'Afternoon';
    if (hour >= 17 || hour < 6) return 'Evening';
    return 'Evening'; // fallback
  };

  // Get gym timezone from first slot (all slots from same gym)
  const gymTimezone = timeSlots.length > 0 ? timeSlots[0].gym_timezone : null;

  // Group slots by time of day
  const groupedSlots = timeSlots.reduce((groups, slot) => {
    const timeOfDay = getTimeOfDay(slot.start_time, gymTimezone);
    if (!groups[timeOfDay]) groups[timeOfDay] = [];
    groups[timeOfDay].push(slot);
    return groups;
  }, {});

  const handleClose = () => {
    setSelectedSlots([]);
    setReservationNotes('');
    setError(null);
    setSelectedDate(getTodayDateString()); // Reset to today
    setDateAvailability({});
    setShowConfirmation(false);
    setHasLoadedInitialData(false);
    onClose();
  };



  if (!board) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Quick Reserve - ${board.name}`}
      size="large"
      footer={
        showConfirmation ? (
          <div className="space-x-3">
            <Button
              onClick={() => setShowConfirmation(false)}
              variant="outline"
            >
              Back to Times
            </Button>
            <Button
              onClick={handleReservation}
              loading={isReserving}
              disabled={isReserving}
              variant="gradient"
            >
              Reserve {formatTime(selectedSlots[0].start_time, gymTimezone)} - {formatTime(selectedSlots[selectedSlots.length - 1].end_time, gymTimezone)}
            </Button>
          </div>
        ) : selectedSlots.length > 0 ? (
          <div className="space-x-3">
            <Button
              onClick={() => setSelectedSlots([])}
              variant="outline"
            >
              Clear Selection
            </Button>
            <Button
              onClick={handleContinue}
              variant="gradient"
            >
              Continue
            </Button>
          </div>
        ) : (
          <div className="space-x-3">
            <Button onClick={handleClose} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={() => window.location.href = `/board/${board.id}`}
              variant="outline"
            >
              View Full Schedule
            </Button>
          </div>
        )
      }
    >
      {isCheckingAvailability || (isOpen && timeSlots.length === 0 && !error && Object.keys(dateAvailability).length === 0) ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" text="Checking availability..." />
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" text={`Loading available times${selectedDate === getTodayDateString() ? ' for today' : ''}...`} />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-lg bg-coral-100 border-2 border-coral-500 shadow-retro">
            <svg className="h-8 w-8 text-coral-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="mt-6 text-lg font-bold text-slate-900">Unable to load time slots</h3>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <div className="mt-6">
            <Button onClick={loadTimeSlots}>Try Again</Button>
          </div>
        </div>
      ) : showConfirmation && selectedSlots.length > 0 ? (
        // Reservation confirmation step
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-teal-500 to-magenta-500 mr-2 rounded"></span>
              Confirm Reservation
            </h3>
            <div className="bg-gradient-to-br from-teal-50 to-purple-50 rounded-lg p-5 border-2 border-slate-900 shadow-retro">
              <div className="flex items-center">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-teal-500 border-2 border-slate-900 shadow-retro mr-4 flex-shrink-0">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-700">
                    {board.name} • {selectedDate === getTodayDateString() ? 'Today' : selectedDate}
                  </p>
                  <p className="text-lg font-bold text-slate-900">
                    {formatTime(selectedSlots[0].start_time, gymTimezone)} - {formatTime(selectedSlots[selectedSlots.length - 1].end_time, gymTimezone)}
                  </p>
                  {selectedSlots.length > 1 && (
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedSlots.length} consecutive time slots ({Math.round((new Date(selectedSlots[selectedSlots.length - 1].end_time) - new Date(selectedSlots[0].start_time)) / (1000 * 60))} minutes total)
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="quick-notes" className="block text-sm font-semibold text-slate-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              id="quick-notes"
              rows={3}
              value={reservationNotes}
              onChange={(e) => setReservationNotes(e.target.value)}
              className="block w-full px-3 py-2 border-2 border-slate-900 rounded-lg shadow-retro focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              placeholder="Add any notes about your training session..."
              maxLength={500}
            />
          </div>

          {error && (
            <div className="bg-coral-50 border-2 border-coral-500 rounded-lg p-4 shadow-retro">
              <div className="flex">
                <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-coral-500 border-2 border-slate-900 shadow-retro flex-shrink-0">
                  <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-bold text-coral-900">Reservation Error</h3>
                  <p className="mt-1 text-sm text-coral-800">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : timeSlots.length === 0 ? (
        // No slots available - still show date selector
        <div className="space-y-6">
          {/* Date Selection */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-teal-500 to-magenta-500 mr-2 rounded"></span>
              Select Date & Time
            </h3>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Choose a date
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                {getAvailableDates().map((date) => {
                  const isDisabled = !date.hasSlots && !isCheckingAvailability;

                  return (
                    <button
                      key={date.value}
                      onClick={() => !isDisabled && setSelectedDate(date.value)}
                      disabled={isDisabled}
                      className={`group p-3 text-center border-2 rounded-lg transition-all focus:outline-none ${
                        isDisabled
                          ? 'bg-slate-100 text-slate-400 border-slate-300 cursor-not-allowed'
                          : selectedDate === date.value
                            ? 'bg-teal-500 text-white border-slate-900 shadow-retro'
                            : 'bg-white text-slate-700 border-slate-900 hover:bg-teal-500 hover:text-white hover:shadow-retro-hover shadow-retro'
                      }`}
                    >
                      <div className="text-sm font-bold">
                        {date.dayLabel}
                      </div>
                      <div className="text-xs mt-1 font-semibold">
                        {date.dateLabel}
                      </div>
                      {date.isToday && (
                        <div className={`text-xs mt-1 font-semibold ${
                          selectedDate === date.value ? 'text-white' : isDisabled ? 'text-slate-400' : 'text-teal-600 group-hover:text-white'
                        }`}>
                          Today
                        </div>
                      )}
                      {!isCheckingAvailability && (
                        <div className={`text-xs mt-1 ${
                          isDisabled
                            ? 'text-slate-500'
                            : selectedDate === date.value
                              ? 'text-white/90'
                              : 'text-slate-600 group-hover:text-white/90'
                        }`}>
                          {isDisabled ? 'No slots' : `${date.slotCount} ${date.slotCount === 1 ? 'slot' : 'slots'}`}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* No slots message */}
          <div className="text-center py-8 bg-white rounded-lg border-2 border-slate-900 shadow-retro">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-lg bg-slate-100 border-2 border-slate-400 shadow-retro mb-4">
              <svg className="h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-sm font-bold text-slate-900">
              No available times {selectedDate === getTodayDateString() ? 'today' : 'for this date'}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {selectedDate === getTodayDateString()
                ? 'All time slots for today are already reserved or have passed.'
                : 'All time slots for this date are already reserved or the gym may be closed.'}
            </p>
            <p className="mt-2 text-sm text-teal-600 font-semibold">
              Try selecting a different date above.
            </p>
          </div>
        </div>
      ) : (
        // Available slots grouped by time of day
        <div className="space-y-6">
          {/* Date Selection */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-teal-500 to-magenta-500 mr-2 rounded"></span>
              Select Date & Time
            </h3>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Choose a date
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                {getAvailableDates().map((date) => {
                  const isDisabled = !date.hasSlots && !isCheckingAvailability;

                  return (
                    <button
                      key={date.value}
                      onClick={() => !isDisabled && setSelectedDate(date.value)}
                      disabled={isDisabled}
                      className={`group p-3 text-center border-2 rounded-lg transition-all focus:outline-none ${
                        isDisabled
                          ? 'bg-slate-100 text-slate-400 border-slate-300 cursor-not-allowed'
                          : selectedDate === date.value
                            ? 'bg-teal-500 text-white border-slate-900 shadow-retro'
                            : 'bg-white text-slate-700 border-slate-900 hover:bg-teal-500 hover:text-white hover:shadow-retro-hover shadow-retro'
                      }`}
                    >
                      <div className="text-sm font-bold">
                        {date.dayLabel}
                      </div>
                      <div className="text-xs mt-1 font-semibold">
                        {date.dateLabel}
                      </div>
                      {date.isToday && (
                        <div className={`text-xs mt-1 font-semibold ${
                          selectedDate === date.value ? 'text-white' : isDisabled ? 'text-slate-400' : 'text-teal-600 group-hover:text-white'
                        }`}>
                          Today
                        </div>
                      )}
                      {!isCheckingAvailability && (
                        <div className={`text-xs mt-1 ${
                          isDisabled
                            ? 'text-slate-500'
                            : selectedDate === date.value
                              ? 'text-white/90'
                              : 'text-slate-600 group-hover:text-white/90'
                        }`}>
                          {isDisabled ? 'No slots' : `${date.slotCount} ${date.slotCount === 1 ? 'slot' : 'slots'}`}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Time Slots */}
          <div>
            <h4 className="text-md font-bold text-slate-900 mb-2">
              Available Times
            </h4>
            <p className="text-sm text-slate-600 mb-2">
              {selectedSlots.length === 0
                ? 'Select a time slot to reserve'
                : `Selected: ${formatTime(selectedSlots[0].start_time, gymTimezone)} - ${formatTime(selectedSlots[0].end_time, gymTimezone)}`
              }
            </p>
            {selectedSlots.length > 0 && (
              <div className="bg-teal-50 border-l-4 border-teal-500 p-3 mb-4">
                <p className="text-sm text-teal-800">
                  Click a different slot to change your selection, or click Continue to proceed
                </p>
              </div>
            )}
          </div>

          {['Morning', 'Afternoon', 'Evening'].map(timeOfDay => {
            const slots = groupedSlots[timeOfDay];
            if (!slots || slots.length === 0) return null;

            return (
            <div key={timeOfDay}>
              <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center">
                <span className="w-1 h-4 bg-teal-500 mr-2 rounded"></span>
                {timeOfDay}
                <span className="text-xs text-slate-500 font-normal ml-2">
                  {timeOfDay === 'Morning'}
                  {timeOfDay === 'Afternoon'}
                  {timeOfDay === 'Evening'}
                </span>
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {slots.map((slot) => {
                  // Calculate duration from start and end times
                  const startTime = new Date(slot.start_time);
                  const endTime = new Date(slot.end_time);
                  const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
                  const isSelected = selectedSlots.some(s => s.id === slot.id);

                  return (
                    <button
                      key={slot.id}
                      onClick={() => handleSlotSelect(slot)}
                      className={`p-3 text-left border-2 rounded-lg shadow-retro focus:outline-none transition-all group ${
                        isSelected
                          ? 'bg-teal-500 text-white border-slate-900 shadow-retro-hover'
                          : 'bg-white border-slate-900 hover:bg-teal-500 hover:text-white hover:shadow-retro-hover'
                      }`}
                    >
                      <div className={`font-bold ${
                        isSelected ? 'text-white' : 'text-slate-900 group-hover:text-white'
                      }`}>
                        {formatTime(slot.start_time, gymTimezone)}
                      </div>
                      <div className={`text-sm ${
                        isSelected ? 'text-teal-100' : 'text-slate-600 group-hover:text-teal-100'
                      }`}>
                        {durationMinutes} min
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            );
          }).filter(Boolean)}
        </div>
      )}
    </Modal>
  );
};

export default QuickReserveModal;
